/** biome-ignore-all lint/a11y/useSemanticElements: <explanation> */
import type Monaco from 'monaco-editor';
import React, { useEffect, useRef, useState } from 'react';
// import * as monaco from 'monaco-editor/esm/vs/editor/editor.api';
import { IField, Modal, TextEditorProps } from 'rewire-ui';
import { IUdf } from '../DataTypes';
import udfsSubscriptionService from '../services/UdfsSubscriptionService';
import Dialog from '../ui/Dialog';

const monaco = await import('monaco-editor');
await import('./userWorker');

class RuleExpressionEditorModel extends Modal<{ notOk: boolean }> {
  private editor: any;

  constructor(
    private field: IField,
    private onValueChange: any,
  ) {
    super('Expression Editor', true);
    this.state.notOk = false;
    this.action('cancel', this.cancel);
    this.action('ok', this.submit, { type: 'submit', disabled: () => this.state.notOk });
  }

  get notOk() {
    return this.state.notOk;
  }

  set notOk(value: boolean) {
    this.state.notOk = value;
  }

  setEditor(editor: any) {
    this.editor = editor;
    this.editor.setValue(this.field.value || '');
  }

  cancel = () => {
    this.onValueChange(this.field.value);
    return true;
  };

  submit = async (): Promise<boolean> => {
    if (this.editor) {
      this.onValueChange(this.editor.getValue());
    }
    return true;
  };
}

function getUdfReferenceType(udf: IUdf) {
  const reference = udf.allowableValues;
  if (reference && typeof reference === 'string') {
    const udfMap = udfs.find((udf) => udf.udf === reference);
    if (udfMap) {
      if (!udfCache[udfMap.udf]?.length) {
        return udfMap.type;
      }
      return `I${udfMap.name}`;
    }
  }
  return 'INameEntity';
}

function getUdfType(udf: IUdf) {
  let type: string = 'string';
  if (udf.hasEffective) {
    type = `(${getUdfReferenceType(udf)} & IUdfEffective)`;
  } else {
    switch (udf.type) {
      case 'boolean':
        type = 'boolean';
        break;

      case 'date':
        type = 'string';
        break;

      case 'integer':
      case 'float':
        type = 'number';
        break;

      case 'reference':
        type = getUdfReferenceType(udf);
        break;

      default:
        type = 'string';
        break;
    }
  }
  if (udf.isList) type = `${type}[]`;
  return type;
}

interface IUdfCache {
  [udf: string]: IUdf[];
}

interface IUdfMap {
  udf: string;
  code: string;
  name: string;
  type: string;
}
const udfs: IUdfMap[] = [
  { udf: 'reasons', code: 'reason', name: 'Reason', type: 'IReason' },
  { udf: 'reasons', code: 'overtimeReason', name: 'OvertimeReason', type: 'IReason' },
  { udf: 'groups', code: 'group', name: 'Group', type: 'IGroup' },
  { udf: 'batches', code: 'batch', name: 'Batch', type: 'IBatch' },
  { udf: 'generators', code: 'shiftCycle', name: 'ShiftCycle', type: 'IShiftCycle' },
  { udf: 'occupations', code: 'occupation', name: 'Occupation', type: 'IOccupation' },
  { udf: 'templates', code: 'shiftType', name: 'ShiftType', type: 'IShiftType' },
  { udf: 'employees', code: 'employee', name: 'Employee', type: 'IEmployee' },
  { udf: 'workOrder', code: 'workOrder', name: 'WorkOrder', type: 'IDocument' },
  { udf: 'timecards', code: 'timecard', name: 'Timecard', type: 'ITimecard' },
  { udf: 'jobs', code: 'job', name: 'Job', type: 'IJob' },
];

let udfCache: IUdfCache = {};

function buildUdf(udfMap: IUdfMap) {
  const udfs = udfCache[udfMap.udf];

  if (!udfs) {
    return '';
  }

  return _buildUdf(udfMap, udfs);
}

function _buildUdf(udfMap: IUdfMap, udfs: IUdf[]) {
  const udfList = udfs?.map((udf) => `udf(p: "${udf.displayName}"): ${getUdfType(udf)};`).join(' ') ?? '';
  return `declare interface I${udfMap.name} {
    ${udfList}
  }
  `;
}

function buildTimecardUdf(udfs: IUdf[]) {
  return udfs?.map((udf) => `declare function udf(p: "${udf.displayName}"): ${getUdfType(udf)};`).join(' ') ?? '';
}

let libs: Record<string, unknown>;
async function initializeEditorIntellisense(intellisenseDefns?: string[]) {
  const intellisenseLibs: any[] = [];
  if (!libs) {
    const js = monaco.languages.typescript;

    js.typescriptDefaults.setCompilerOptions({
      target: monaco.languages.typescript.ScriptTarget.ESNext,
      module: monaco.languages.typescript.ModuleKind.ESNext,
      moduleResolution: monaco.languages.typescript.ModuleResolutionKind.NodeJs,
      noEmit: true,
      noLib: true,
      allowNonTsExtensions: true,
    });

    libs = {
      core: {
        filePath: 'core.d.ts',
        content: (await import('../intellisense/core.d.ts?raw')).default,
      },
      'udf-expression': {
        filePath: 'udf-expression.d.ts',
        content: (await import('../intellisense/udf-expression.d.ts?raw')).default,
      },
      common: {
        filePath: 'common.d.ts',
        content: (await import('../intellisense/common.d.ts?raw')).default,
      },
      'accrual-employee-qualifies': {
        filePath: 'accrual-employee-qualifies.d.ts',
        content: (await import('../intellisense/accrual-employee-qualifies.d.ts?raw')).default,
      },
      'accrual-end-date': {
        filePath: 'accrual-end-date.d.ts',
        content: (await import('../intellisense/accrual-end-date.d.ts?raw')).default,
      },
      'accrual-minimum-hours-worked': {
        filePath: 'accrual-minimum-hours-worked.d.ts',
        content: (await import('../intellisense/accrual-minimum-hours-worked.d.ts?raw')).default,
      },
      'accrual-start-date': {
        filePath: 'accrual-start-date.d.ts',
        content: (await import('../intellisense/accrual-start-date.d.ts?raw')).default,
      },
      'accrual-allocation': {
        filePath: 'accrual-allocation.d.ts',
        content: (await import('../intellisense/accrual-allocation.d.ts?raw')).default,
      },
      'accrual-trigger': {
        filePath: 'accrual-trigger.d.ts',
        content: (await import('../intellisense/accrual-trigger.d.ts?raw')).default,
      },
      'amount-to-apply': {
        filePath: 'amount-to-apply.d.ts',
        content: (await import('../intellisense/amount-to-apply.d.ts?raw')).default,
      },
      overtime: {
        filePath: 'overtime.d.ts',
        content: (await import('../intellisense/overtime.d.ts?raw')).default,
      },
      premium: {
        filePath: 'premium.d.ts',
        content: (await import('../intellisense/premium.d.ts?raw')).default,
      },
      es5: {
        filePath: 'es5.d.ts',
        content: (await import('../intellisense/es5.d.ts?raw')).default,
      },
      exporter: {
        filePath: 'exporter.d.ts',
        content: (await import('../intellisense/exporter.d.ts?raw')).default,
      },
      rate: {
        filePath: 'rate.d.ts',
        content: (await import('../intellisense/rate.d.ts?raw')).default,
      },
      holiday: {
        filePath: 'holiday.d.ts',
        content: (await import('../intellisense/holiday.d.ts?raw')).default,
      },
    };
  }

  let lib = '';
  udfCache = {};

  for (const udf of udfs) {
    if (udfCache[udf.udf]) {
      continue;
    }
    udfCache[udf.udf] = await udfsSubscriptionService.subscriptionModel.getUdfsByDocumentType(udf.udf);
  }

  for (const udf of udfs) {
    lib = `${lib} ${buildUdf(udf)}`;
  }

  lib = `${lib} ${buildTimecardUdf(udfCache['timecards'] || [])}`;

  intellisenseLibs.push(libs['es5']);
  intellisenseLibs.push(libs['core']);

  if (intellisenseDefns) {
    for (const lib of intellisenseDefns) {
      const l = libs[lib];
      if (l) {
        intellisenseLibs.push(l);
      }
    }
    intellisenseLibs.push({ content: lib, filePath: 'udf.d.ts' });
  }

  // eslint-disable-next-line no-inner-declarations
  monaco.languages.typescript.typescriptDefaults.setExtraLibs(intellisenseLibs);
}

export function createExpressionEditor(intellisenseDefns?: string[]) {
  const Editor = ({ field, onValueChange }: TextEditorProps) => {
    const ruleExpressionEditorModel = useRef(new RuleExpressionEditorModel(field, onValueChange));
    const [editor, setEditor] = useState<Monaco.editor.IStandaloneCodeEditor | null>(null);
    const monacoEl = useRef(null);

    useEffect(() => {
      requestAnimationFrame(async () => {
        if (monacoEl.current && !editor) {
          await initializeEditorIntellisense(intellisenseDefns);
          setEditor((editor) => {
            if (editor) return editor;

            const _editor = monaco.editor.create(monacoEl.current!, {
              value: '',
              suggest: {
                showKeywords: false,
                showClasses: false,
                showConstructors: false,
                showEnums: false,
                showModules: false,
                showInterfaces: false,
              },
              scrollBeyondLastLine: false,
              bracketPairColorization: { enabled: true },
              language: 'typescript',
              minimap: {
                enabled: false,
              },
              fixedOverflowWidgets: true,
              lineNumbers: 'off',
            });
            ruleExpressionEditorModel.current.setEditor(_editor);
            return _editor;
          });
        }
      });
      return () => {
        editor?.getModel()?.dispose();
        editor?.dispose();
      };
    }, [monacoEl.current]);

    useEffect(() => {
      if (!editor) return;

      const changeMarkers = monaco.editor.onDidChangeMarkers(([uri]) => {
        const markers = monaco.editor.getModelMarkers({ resource: uri });
        ruleExpressionEditorModel.current.notOk = markers && markers.length > 0;
      });
      return () => {
        changeMarkers?.dispose();
      };
    }, [editor]);

    function handleKeyDown(evt: React.KeyboardEvent<any>) {
      switch (evt.key) {
        case 'F1':
          evt.stopPropagation();
          evt.preventDefault();
          break;

        case 'Escape':
          // this._value = this.value;
          evt.stopPropagation();
          break;

        case 'Enter':
          if (evt.ctrlKey && editor) {
            ruleExpressionEditorModel.current.submit().then(() => {
              ruleExpressionEditorModel.current.close();
            });
            evt.preventDefault();
            evt.stopPropagation();
            return;
          }
          evt.stopPropagation();
          break;

        case '*':
          editor?.trigger('keyboard', 'type', { text: '*' });
          evt.stopPropagation();
          break;

        default:
          evt.stopPropagation();
          break;
      }
    }

    return (
      <Dialog dialog={ruleExpressionEditorModel.current} disableTransition={true} maxWidth="lg">
        <div
          style={{ width: '100%', height: '100%', minHeight: 800 }}
          ref={monacoEl}
          onKeyDown={handleKeyDown}
          role="textbox"
          tabIndex={0}
        />
      </Dialog>
    );
  };

  return (props: TextEditorProps) => {
    return <Editor {...props} />;
  };
}
